import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PHCalculator = () => {
  const [inputData, setInputData] = useState({
    hydrogenConcentration: '',
    hydroxideConcentration: '',
    calculateType: 'pH' // Options: 'pH', 'pOH'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCalculateTypeChange = (e) => {
    setInputData(prev => ({
      ...prev,
      calculateType: e.target.value
    }));
    setError('');
  };

  const calculatePH = () => {
    const { hydrogenConcentration, hydroxideConcentration, calculateType } = inputData;

    // Validate inputs
    if ((calculateType === 'pH' && !hydrogenConcentration) || 
        (calculateType === 'pOH' && !hydroxideConcentration)) {
      setError('Please enter the required value');
      return;
    }

    let cleanHydrogenConcentration, cleanHydroxideConcentration;

    if (hydrogenConcentration) {
      cleanHydrogenConcentration = parseFloat(hydrogenConcentration.replace(/\s/g, ''));
      if (isNaN(cleanHydrogenConcentration) || cleanHydrogenConcentration <= 0) {
        setError('Please enter a valid positive number for hydrogen ion concentration');
        return;
      }
    }

    if (hydroxideConcentration) {
      cleanHydroxideConcentration = parseFloat(hydroxideConcentration.replace(/\s/g, ''));
      if (isNaN(cleanHydroxideConcentration) || cleanHydroxideConcentration <= 0) {
        setError('Please enter a valid positive number for hydroxide ion concentration');
        return;
      }
    }

    let pH, pOH;

    if (calculateType === 'pH') {
      pH = -Math.log10(cleanHydrogenConcentration);
      pOH = 14 - pH;
    } else {
      pOH = -Math.log10(cleanHydroxideConcentration);
      pH = 14 - pOH;
    }

    // Create visualization data
    const chartData = {
      labels: ['pH', 'pOH'],
      datasets: [
        {
          label: 'Value',
          data: [pH, pOH],
          backgroundColor: ['rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    };

    setResults({
      pH: pH.toFixed(2),
      pOH: pOH.toFixed(2),
      hydrogenConcentration: cleanHydrogenConcentration?.toExponential(2),
      hydroxideConcentration: cleanHydroxideConcentration?.toExponential(2),
      chartData
    });
  };

  const exampleData = {
    hydrogenConcentration: '1e-7',
    hydroxideConcentration: '1e-7'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-pink-100 to-purple-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>pH Calculator | Chemistry Tool</title>
          <meta name="description" content="Calculate pH and pOH values easily with this tool. This calculator helps in quick chemical calculations for acidity and alkalinity measurements." />
          <meta name="keywords" content="pH calculator, chemistry calculator, pOH calculator, acidity measurement" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🧪</div>
            <h1 className="text-3xl font-bold text-gray-800">pH Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate pH and pOH values easily. This tool helps in quick chemical calculations for acidity and alkalinity measurements.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculate:</label>
              <select
                name="calculateType"
                value={inputData.calculateType}
                onChange={handleCalculateTypeChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="pH">pH from Hydrogen Ion Concentration</option>
                <option value="pOH">pOH from Hydroxide Ion Concentration</option>
              </select>
            </div>

            {inputData.calculateType === 'pH' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Hydrogen Ion Concentration [H⁺] (mol/L):</label>
                <input
                  type="text"
                  name="hydrogenConcentration"
                  value={inputData.hydrogenConcentration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Enter hydrogen ion concentration (e.g., 1e-7)"
                />
              </div>
            )}

            {inputData.calculateType === 'pOH' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Hydroxide Ion Concentration [OH⁻] (mol/L):</label>
                <input
                  type="text"
                  name="hydroxideConcentration"
                  value={inputData.hydroxideConcentration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Enter hydroxide ion concentration (e.g., 1e-7)"
                />
              </div>
            )}
          </div>

          <button
            onClick={calculatePH}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate pH
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">pH Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Input Details</h3>
                  <div className="space-y-3">
                    {inputData.calculateType === 'pH' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Hydrogen Ion Concentration [H⁺]</span>
                          <span>{results.hydrogenConcentration}</span>
                        </div>
                      </>
                    )}
                    {inputData.calculateType === 'pOH' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Hydroxide Ion Concentration [OH⁻]</span>
                          <span>{results.hydroxideConcentration}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Results</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">pH</span>
                      <span className="font-bold text-pink-600">{results.pH}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">pOH</span>
                      <span className="font-bold text-pink-600">{results.pOH}</span>
                    </div>
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Measurement'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'pH and pOH Values'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Explanation</h3>
                <div className="space-y-4">
                  <p>
                    {inputData.calculateType === 'pH' 
                      ? `The pH was calculated using the formula: pH = -log₁₀([H⁺])` 
                      : `The pOH was calculated using the formula: pOH = -log₁₀([OH⁻])`}
                  </p>
                  <p>
                    The relationship between pH and pOH is: pH + pOH = 14
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-pink-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for pH and pOH:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Hydrogen Ion Concentration: {exampleData.hydrogenConcentration}</li>
                <li>Hydroxide Ion Concentration: {exampleData.hydroxideConcentration}</li>
              </ul>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>pH: 7.00</li>
                <li>pOH: 7.00</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-pink-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the pH Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Calculation Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose whether to calculate pH or pOH</li>
                </ul>
              </li>
              <li><strong>Enter Concentration:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the hydrogen ion concentration for pH calculation</li>
                  <li>Input the hydroxide ion concentration for pOH calculation</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will:
                <ul className="list-disc pl-6 mt-2">
                  <li>Compute the pH or pOH value</li>
                  <li>Show the complementary value (pOH or pH)</li>
                  <li>Display a visual comparison</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-pink-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use scientific notation (e.g., 1e-7) for very small or large values</li>
                <li>Remember that pH values below 7 are acidic, and values above 7 are basic</li>
                <li>At 25°C, pH + pOH = 14</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">pH and pOH Guide <span className="text-pink-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>pH Scale:</strong> Measures the acidity or alkalinity of a solution (0-14)</li>
              <li><strong>pOH Scale:</strong> Measures the basicity of a solution (0-14)</li>
              <li><strong>Neutral Solution:</strong> pH = 7, pOH = 7</li>
              <li><strong>Acidic Solution:</strong> pH {'<'} 7, pOH {'>'} 7</li>
              <li><strong>Basic Solution:</strong> pH {'>'} 7, pOH {'<'} 7</li>
            </ul>
            <p className="italic mt-4">
              Note: These calculations are based on the assumption of standard temperature (25°C) and ideal conditions.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PHCalculator;